import { HttpError } from '@magicdoorinc/errors';
import type { HttpErrorJson } from '@magicdoorinc/errors';

export type LeasingErrorCode =
  | 'application_already_submitted'
  | 'application_locked'
  | 'application_not_submitted'
  | 'application_already_retracted'
  | 'application_not_qualified'
  | 'info_update_request_expired'
  | 'info_update_request_already_completed'
  | 'no_rental_bank_account'
  | 'company_payment_not_configured'
  | 'payment_not_required'
  | 'payment_already_recorded'
  | 'bifurcated_screening_not_accepted'
  | 'rental_application_rescore_rate_limited'
  | 'bad_request';

export interface LeasingProblemJson extends HttpErrorJson {
  errorCode?: LeasingErrorCode | string;
  retryAfterSeconds?: number;
  extensions?: {
    errors?: string[];
    errorCode?: string;
    retryAfterSeconds?: number;
    [key: string]: unknown;
  };
}

export const getLeasingErrorCode = (error: unknown): LeasingErrorCode | undefined => {
  if (!(error instanceof HttpError)) return undefined;
  const json = error.getJson<LeasingProblemJson>();
  const code = json?.errorCode ?? json?.extensions?.errorCode;
  return code as LeasingErrorCode | undefined;
};

export const getLeasingErrorDetail = (error: unknown): string | undefined => {
  if (!(error instanceof HttpError)) return undefined;
  const json = error.getJson<LeasingProblemJson>();
  return json?.detail;
};

export const getLeasingRetryAfterSeconds = (error: unknown): number | undefined => {
  if (!(error instanceof HttpError)) return undefined;
  const json = error.getJson<LeasingProblemJson>();
  return json?.retryAfterSeconds ?? json?.extensions?.retryAfterSeconds;
};

export const getLeasingValidationErrors = (error: unknown): string[] => {
  if (!(error instanceof HttpError)) return [];
  const json = error.getJson<LeasingProblemJson>();
  if (Array.isArray(json?.extensions?.errors)) return json!.extensions!.errors!;
  if (json?.errors) {
    return Object.values(json.errors).flat();
  }
  return [];
};

export const leasingErrorMessageKey: Record<LeasingErrorCode, string> = {
  application_already_submitted: 'This application has already been submitted',
  application_locked: 'This application is locked and cannot be edited',
  application_not_submitted: 'A decision can only be set on submitted applications',
  application_already_retracted: 'This application has already been retracted',
  application_not_qualified: 'The application is not yet ready to submit',
  info_update_request_expired: 'This info update request has expired — issue a new one to send a fresh link',
  info_update_request_already_completed: 'The applicant has already used this link',
  no_rental_bank_account: 'A rental bank account is required before payment can be taken',
  company_payment_not_configured: 'Payments are not yet configured for this company',
  payment_not_required: "Payment isn't required for this application",
  payment_already_recorded: 'A payment has already been recorded for this application',
  bifurcated_screening_not_accepted: 'Confirmation required before viewing the criminal report',
  rental_application_rescore_rate_limited: 'Genie was just re-scored — please try again later',
  bad_request: 'Invalid request',
};
